import { motion } from "framer-motion";
import { Phone, MessageCircle, Send, Instagram, Youtube, AtSign, Mail } from "lucide-react";
import { APPLY_URL, CONTACT, ASSETS } from "../lib/constants";

const ease = [0.76, 0, 0.24, 1];

const socialCircles = [
  { key: "phone", href: `tel:${CONTACT.phoneIntl}`, Icon: Phone, label: "Phone" },
  { key: "whatsapp", href: CONTACT.whatsapp, Icon: MessageCircle, label: "WhatsApp" },
  { key: "telegram", href: CONTACT.telegram, Icon: Send, label: "Telegram" },
  { key: "instagram", href: CONTACT.instagram, Icon: Instagram, label: "Instagram" },
  { key: "youtube", href: CONTACT.youtube, Icon: Youtube, label: "YouTube" },
  { key: "threads", href: CONTACT.threads, Icon: AtSign, label: "Threads" },
  { key: "email", href: `mailto:${CONTACT.email}`, Icon: Mail, label: "Email" },
];

const instituteLinks = [
  { label: "About", href: "#about" },
  { label: "Method", href: "#method" },
  { label: "Mentor", href: "#mentor" },
  { label: "Voices", href: "#testimonials" },
  { label: "2027 Program", href: "#program" },
  { label: "FAQ", href: "#faq" },
];

const contactLines = [
  { label: "Call", value: CONTACT.phone, href: `tel:${CONTACT.phoneIntl}` },
  { label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  { label: "Instagram", value: "@NavSankalp.ias", href: CONTACT.instagram },
  { label: "YouTube", value: "@NavSankalpIAS", href: CONTACT.youtube },
  { label: "Free Orientation", value: "1st week of August", href: APPLY_URL },
];

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-ink text-cream pt-20 md:pt-28 pb-10 md:pb-14 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 55% 40% at 90% 20%, rgba(217,105,28,0.10), transparent 60%)",
        }}
      />

      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16 relative">
        {/* Big pre-footer CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.0, ease }}
          className="pb-16 md:pb-24 border-b border-cream/15"
        >
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
            <div className="col-span-12 lg:col-span-8">
              <h2
                className="font-display font-light text-5xl md:text-7xl lg:text-[8rem] leading-[0.92] tracking-[-0.03em] text-cream"
                style={{ fontVariationSettings: '"SOFT" 30' }}
              >
                Let's begin <br />
                <span className="italic text-saffron">your resolve.</span>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-4">
              <p className="text-cream/75 text-lg leading-relaxed font-light max-w-md mb-8">
                Write, call or message. Replies are personal — from the mentor himself.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={APPLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-apply-btn"
                  className="mag-btn bg-saffron text-cream hover:text-cream px-7 py-4 text-xs tracking-[0.24em] uppercase font-semibold border border-saffron rounded-full"
                >
                  Register for Free Updates →
                </a>
                <a
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="inline-flex items-center px-7 py-4 text-xs tracking-[0.24em] uppercase font-semibold border border-cream/30 text-cream hover:border-saffron hover:text-saffron transition-colors duration-300 rounded-full"
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Footer columns */}
        <div className="pt-14 md:pt-20 grid grid-cols-12 gap-10 md:gap-16">
          {/* Col 1: Brand */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
            className="col-span-12 md:col-span-6 lg:col-span-5"
          >
            <div className="flex items-start gap-4 mb-8">
              <img src={ASSETS.logo} alt="NavSankalpIAS" className="h-11 w-auto object-contain" />
              <div>
                <div
                  className="font-display text-2xl md:text-3xl text-cream leading-none"
                  style={{ fontVariationSettings: '"SOFT" 40' }}
                >
                  NavSankalp IAS
                </div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-saffron font-semibold mt-2">
                  UPSC 2027 Mentorship
                </div>
              </div>
            </div>
            <p className="text-cream/70 text-base leading-relaxed font-light max-w-sm">
              Mentor-led, structure-first preparation for aspirants who refuse to spend three years drifting.
            </p>

            {/* Social circle icons */}
            <div className="mt-8 flex flex-wrap gap-3">
              {socialCircles.map(({ key, href, Icon, label }) => (
                <a
                  key={key}
                  href={href}
                  target={key === "phone" || key === "email" ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`contact-${key}`}
                  className="w-11 h-11 rounded-full border border-cream/30 flex items-center justify-center text-cream/80 hover:text-saffron hover:border-saffron transition-colors duration-300"
                >
                  <Icon size={16} strokeWidth={1.4} />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Col 2: Institute links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1, ease }}
            className="col-span-6 md:col-span-3 lg:col-span-3"
          >
            <div className="text-[10px] tracking-[0.32em] uppercase text-saffron font-semibold mb-6">
              Institute
            </div>
            <ul className="space-y-3">
              {instituteLinks.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    className="text-cream/80 hover:text-saffron transition-colors duration-300 text-base md:text-lg font-light"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Col 3: Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2, ease }}
            className="col-span-6 md:col-span-3 lg:col-span-4"
          >
            <div className="text-[10px] tracking-[0.32em] uppercase text-saffron font-semibold mb-6">
              Contact
            </div>
            <ul className="space-y-3">
              {contactLines.map((c) => (
                <li key={c.label}>
                  <a
                    href={c.href}
                    target={c.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="group inline-flex items-baseline gap-2 text-cream/80 hover:text-saffron transition-colors duration-300 text-base md:text-lg font-light"
                  >
                    <span className="text-cream/50 group-hover:text-saffron/80">{c.label}:</span>
                    <span>{c.value}</span>
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 md:mt-20 pt-6 border-t border-cream/15 flex flex-wrap items-center justify-between gap-4">
          <p
            className="font-display italic text-lg md:text-xl text-cream/60"
            style={{ fontVariationSettings: '"SOFT" 100' }}
          >
            सत्यमेव जयते
          </p>
          <p className="text-[10px] tracking-[0.28em] uppercase text-cream/40 font-medium">
            © {new Date().getFullYear()} NavSankalpIAS · All rights reserved
          </p>
        </div>
      </div>
    </section>
  );
}
